import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import notificationService from '../services/notificationService';
import { initializeRealtimeNotifications, disconnectRealtimeNotifications } from '../services/realtimeService';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile and role
  const fetchUserProfile = async (userId, userMetadata = null, session = null) => {
    try {
      console.log('Fetching user profile for:', userId);
      console.log('User metadata:', userMetadata);
      
      // First check user metadata for role (from Supabase Auth)
      if (userMetadata?.role) {
        console.log('Found role in metadata:', userMetadata.role);
        
        if (userMetadata.role === 'merchant') {
          console.log('User is a merchant, creating merchant profile');
          const merchantProfile = {
            id: userId,
            name: userMetadata.full_name || 'Merchant',
            business_name: userMetadata.full_name || 'My Store',
            role: 'merchant',
            isMerchant: true,
            isAdmin: false,
            token: session?.access_token // Add token to profile
          };
          console.log('Created merchant profile:', merchantProfile);
          return merchantProfile;
        } else if (userMetadata.role === 'admin') {
          console.log('User is an admin, creating admin profile');
          return {
            id: userId,
            name: userMetadata.full_name || 'Admin',
            role: 'admin',
            isMerchant: false,
            isAdmin: true,
            token: session?.access_token // Add token to profile
          };
        }
      }

      // If no role in metadata, default to admin
      console.log('No role found in metadata, defaulting to admin');
      return {
        id: userId,
        name: userMetadata?.full_name || 'User',
        role: 'admin',
        isMerchant: false,
        isAdmin: true,
        token: session?.access_token // Add token to profile
      };
      
    } catch (error) {
      console.error('Error in fetchUserProfile:', error);
      // Return a safe default profile
      return {
        id: userId,
        name: userMetadata?.full_name || 'User',
        role: 'admin',
        isMerchant: false,
        isAdmin: true,
        token: session?.access_token // Add token to profile
      };
    }
  };

  useEffect(() => {
    console.log('AuthContext: Setting up auth listeners');
    
    // Listen for auth state changes
    const { data: listener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      console.log('Auth state changed:', _event, session?.user?.id);
      setSession(session);
      if (session?.user) {
        try {
          const profile = await fetchUserProfile(session.user.id, session.user.user_metadata, session);
          setUserProfile(profile);
          
          // Initialize push notifications for authenticated users (non-blocking)
          if (profile && session?.access_token) {
            try {
              console.log('🔔 Initializing notifications for user:', profile.id);
              await notificationService.initializeNotifications(session.access_token);
              
              // Initialize realtime notifications
              console.log('📡 Initializing realtime notifications for:', profile.role);
              await initializeRealtimeNotifications(
                profile.id, 
                session.access_token, 
                profile.role || 'admin'
              );
            } catch (notifError) {
              console.warn('⚠️ Failed to initialize notifications (non-critical):', notifError);
              // Continue without notifications - don't block the login
            }
          }
        } catch (error) {
          console.error('Error fetching profile on auth change:', error);
          setUserProfile(null);
        }
      } else {
        console.log('🔌 User logged out, disconnecting realtime notifications');
        setUserProfile(null);
        // Disconnect realtime notifications on logout
        disconnectRealtimeNotifications();
      }
      setLoading(false);
    });

    // Restore session on mount
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      console.log('Restoring session:', session?.user?.id);
      setSession(session);
      if (session?.user) {
        try {
          const profile = await fetchUserProfile(session.user.id, session.user.user_metadata, session);
          setUserProfile(profile);
          
          // Initialize push notifications for authenticated users
          if (profile && session?.access_token) {
            try {
              console.log('🔔 Initializing notifications for user (session restore):', profile.id);
              await notificationService.initializeNotifications(session.access_token);
              
              // Initialize realtime notifications
              console.log('📡 Initializing realtime notifications (session restore) for:', profile.role);
              await initializeRealtimeNotifications(
                profile.id, 
                session.access_token, 
                profile.role || 'admin'
              );
            } catch (notifError) {
              console.warn('⚠️ Failed to initialize notifications (non-critical):', notifError);
              // Continue without notifications - don't block the login
            }
          }
        } catch (error) {
          console.error('Error fetching profile on session restore:', error);
          setUserProfile(null);
        }
      }
      setLoading(false);
    }).catch(error => {
      console.error('Error getting session:', error);
      setLoading(false);
    });

    return () => {
      console.log('AuthContext: Cleaning up listeners');
      listener?.subscription?.unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    
    if (data.session?.user) {
      const profile = await fetchUserProfile(data.session.user.id, data.session.user.user_metadata, data.session);
      setUserProfile(profile);
    }
    
    setSession(data.session);
    return data.session;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUserProfile(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading authentication...</p>
          <p className="text-sm text-gray-500 mt-2">Please wait while we set up your dashboard</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{
      session,
      user: session?.user,
      userProfile,
      token: session?.access_token,
      login,
      logout,
      isAuthenticated: !!session,
      loading,
      isMerchant: userProfile?.isMerchant || false,
      isAdmin: userProfile?.isAdmin || false,
      role: userProfile?.role || 'admin'
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}